import { useEffect, useState } from 'react'
import { HttpVerb } from '../../../../../../../types/api-types'
import { WellYieldDrawdownFields } from '../../../../../../../types/form-types'
import { FormDelegate, WellMode } from '../../../../../../../types/routing-types'
import ConfirmTransition from '../../../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../../../components/errors/messages'
import { DrillingTestEditForm } from '../../../../../../components/well-forms/drilling-tests/drilling-test-edit-form'
import NotesWrapperEdit from '../../../../../../components/well-forms/notes/notes-wrapper-edit'
import WellEditButtons from '../../../../../../components/wells/well-edit-buttons'
import { useWellState } from '../../../../../../context/well-context'
import { useGetWellData, useSaveWellData } from '../../../../../../hooks/use-api'
import useEditForm from '../../../../../../hooks/use-edit-form'
import useToastMessage from '../../../../../../hooks/use-toast-message'
import { getRandomID } from '../../../../../../services/form-utils'
import { useParamsOr404 } from '../../../../../../hooks/use-params-or-404'
import { useNavigate } from 'react-router-dom'
import { RecordFooter, useTitleChange } from '@msaf/core-react'

function DrillingTestEdit({ setIsDirty }: FormDelegate) {
  const {
    wellState: { isDraft },
  } = useWellState()
  useTitleChange(`${isDraft ? 'New' : 'Edit'} Well | Drilling Test`)
  const { id: wellId, testId } = useParamsOr404('id', 'testId')
  const isNewDrillingTest = testId === WellMode.NEW

  const getUrl = `wells/${wellId}/drilling-tests/${testId}/edit`
  const saveUrl = isNewDrillingTest
    ? `wells/${wellId}/drilling-tests/`
    : `wells/${wellId}/drilling-tests/${testId}/edit`
  const method = isNewDrillingTest ? HttpVerb.POST : HttpVerb.PUT
  const backUrl = `/wells/${wellId}/edit/drilling-tests`
  const viewUrl = `/wells/${wellId}/view/drilling-tests/${testId}`
  const navigate = useNavigate()
  const stateQueryResult = useGetWellData(getUrl)
  const saveMutationResult = useSaveWellData(saveUrl, method)

  const {
    fields,
    isLoading,
    isSaveError,
    error,
    setValue,
    handleSave,
    isSaved,
    removeField,
    addField,
    editField,
    validationErrors,
  } = useEditForm<WellYieldDrawdownFields>({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Save successful',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  useEffect(() => {
    // For add page default to new temp id.
    if (isNewDrillingTest) {
      setValue('id', getRandomID())
    }

    // Disable dependency rule to allow effect to run on page mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cancelHandler = () => {
    isNewDrillingTest ? navigate(backUrl) : navigate(viewUrl)
  }

  return (
    <>
      <DrillingTestEditForm
        fields={fields}
        setValue={setValue}
        isLoading={isLoading}
        removeField={removeField}
        addField={addField}
        validationErrors={validationErrors}
      />
      <RecordFooter
        areActionsFixed={isNewDrillingTest}
        notes={
          <NotesWrapperEdit
            notes={fields.notes ?? []}
            isLoading={isLoading}
            addField={(note) => addField('notes', note, true)}
            removeField={(index: number) => removeField('notes', index)}
            editField={(note, idx) => editField('notes', note, idx)}
            setIsDirty={setIsDirty}
          />
        }
      >
        <WellEditButtons
          actions={[
            {
              label: 'Save and return',
              buttonStyle: 'primary',
              onClick: () =>
                handleSave(async (id, refetch) => {
                  await refetch?.()
                  navigate(backUrl, { state: { isProgrammaticTransition: true } })
                }),
            },
            {
              label: 'Save',
              buttonStyle: 'primary',
              onClick: () =>
                handleSave(async (id, refetch) => {
                  await refetch?.()
                  isNewDrillingTest &&
                    navigate(`/wells/${wellId}/edit/drilling-tests/${id}`, {
                      state: { isProgrammaticTransition: true },
                    })
                }),
            },
            {
              label: isNewDrillingTest ? 'Cancel' : 'Return to view',
              buttonStyle: 'text-action',
              onClick: () => cancelHandler(),
            },
          ]}
        />
      </RecordFooter>
    </>
  )
}

export default function DrillingTestEditWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <DrillingTestEdit setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
